import ListCard from '@Components/ListCard/ListCard';
import AssetUnassignModal from '@Components/Modals/AssetUnassign/UnassignModal';
import OffboardingPDFModal from '@Components/Modals/OffboardingPDF/OffboardingPDFModal';
import usePagination from '@Hooks/usePagination';
import UserServices from '@Services/User.services.ts';
import { Accessory, Asset, ElementToDelete, UserAssetHistory } from '@Types/Asset.types';
import { PaginatedResponse } from '@Types/Response.types';
import { KGPortalUser } from '@Types/User.type';
import {
  ELEMENT_TO_DELETE_TYPE,
  PAGE_SIZE,
  PAGINATED_DEFAULT,
  PATHS,
  PERSISTENT_FILTER_PROP,
  TAB_VALUES,
  TABLE_STRUCTURES,
} from '@Utils/constants';
import { formatAssetHistoryForUser, formatElementsToDelete, sortDescendingArrayByDate } from '@Utils/format.utils.tsx';
import { handleRequestError } from '@Utils/request.utils';
import { useApolloClient } from '@apollo/client';
import { Button, Icons, Pagination, ProfilePicture, SectionCard, useTopHat } from '@kg-internal/pv3-uikit';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { GetEmployeeByIdDocument, GetEmployeeByIdQuery } from '~/__generated__/graph';
import styles from './UserDetails.module.scss';

const UserDetails = () => {
  const [accessoriesCurrentPage, setAccessoriesCurrentPage] = useState<number>(1);
  const [assetsCurrentPage, setAssetsCurrentPage] = useState<number>(1);
  const [assetHistoryCurrentPage, setAssetHistoryCurrentPage] = useState<number>(1);
  const [copied, setCopied] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [foundUser, setFoundUser] = useState<KGPortalUser>();
  const [loading, setLoading] = useState<boolean>(true);
  const [showOffboardingModal, setShowOffboardingModal] = useState<boolean>(false);
  const [elementsToDelete, setElementsToDelete] = useState<ElementToDelete[]>([]);
  const [unassingModalOpen, setUnassingModalOpen] = useState<boolean>(false);
  const [userHasAssets, setUserHasAssets] = useState<boolean>(false);
  const { id } = useParams();
  const block = 'user-details';
  const client = useApolloClient();
  const navigate = useNavigate();

  const [userAssets, setUserAssets] = useState<PaginatedResponse<Asset>>({
    ...PAGINATED_DEFAULT,
  });
  const [userAccessories, setUserAccessories] = useState<PaginatedResponse<Accessory>>({
    ...PAGINATED_DEFAULT,
  });

  const [assetHistory, setAssetHistory] = useState<PaginatedResponse<UserAssetHistory>>({
    ...PAGINATED_DEFAULT,
  });

  const { currentData: userAssetsData, jump: userAssetsJump } = usePagination(
    userAssets.docs || [],
    PAGE_SIZE,
    undefined,
  );

  const { currentData: userAccessoriesData, jump: userAccessoriesJump } = usePagination(
    userAccessories.docs || [],
    PAGE_SIZE,
    undefined,
  );

  const { currentData: assetHistoryData, jump: assetHistoryJump } = usePagination(
    assetHistory.docs || [],
    PAGE_SIZE,
    undefined,
  );

  const [searchParams] = useSearchParams();

  const getUserHref = () => {
    const data = searchParams.get(PERSISTENT_FILTER_PROP);
    if (!data) {
      return `${PATHS.USERS}?tab=${TAB_VALUES.USERS}&page=${1}`;
    }
    return `${PATHS.USERS}${data}`;
  };

  const usersBreadcrumb = {
    title: 'Users',
    href: getUserHref(),
    id: 'Users',
  };

  const userNameBreadcrumb = (breadcrumb: string) => ({
    title: breadcrumb,
    href: `.`,
    id: 'User Details',
  });

  const { setTopHat } = useTopHat({
    activeNav: 'nav-users',
    activeTab: undefined,
    tabs: [],
    breadcrumbs: [usersBreadcrumb, userNameBreadcrumb('User Details')],
  });

  const getUser = async () => {
    setLoading(true);
    if (id) {
      try {
        const { data: user } = await client.query<GetEmployeeByIdQuery>({
          query: GetEmployeeByIdDocument,
          variables: { personId: +id },
        });

        setFoundUser(user.person!);
        setTopHat({
          breadcrumbs: [usersBreadcrumb, userNameBreadcrumb(user.person!.fullName!)],
        });
        setErrorMessage(undefined);
      } catch (_error) {
        setErrorMessage('Error loading data');
      }
    }
  };

  const getUserAssets = () => {
    UserServices.getUserAssets(foundUser?.email!)
      .then(({ data }) => {
        setUserAssets((prev) => ({ ...prev, docs: data.currentAssets }));
        setUserAccessories((prev) => ({ ...prev, docs: data.accessories }));
        setAssetHistory((prev) => ({
          ...prev,
          docs: sortDescendingArrayByDate(
            formatAssetHistoryForUser(data.previousAssets, foundUser?.email ?? ''),
            'assignmentDate',
          ),
        }));
        setUserHasAssets(
          data.currentAssets.length > 0 || data.previousAssets.length > 0 || data.accessories.length > 0,
        );
        setErrorMessage(undefined);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getUserAssets();
  }, [foundUser]);

  useEffect(() => {
    getUser();
  }, []);

  const clickProfileHandler = () => {
    navigator.clipboard.writeText(foundUser?.email || '');
    setCopied(true);
  };

  const handleUnassignModalClose = () => {
    setUnassingModalOpen(false);
    setElementsToDelete([]);
  };

  const handleUnassign = (
    elementId: string,
    type: (typeof ELEMENT_TO_DELETE_TYPE)[keyof typeof ELEMENT_TO_DELETE_TYPE],
  ) => {
    setElementsToDelete([{ id: elementId, type }]);
    setUnassingModalOpen(true);
  };

  const handleUnassignAll = () => {
    const allElements = [
      ...formatElementsToDelete(userAssets.docs, ELEMENT_TO_DELETE_TYPE.ASSET),
      ...formatElementsToDelete(userAccessories.docs, ELEMENT_TO_DELETE_TYPE.ACCESSORY),
    ];
    setElementsToDelete(allElements);
    setUnassingModalOpen(true);
  };

  return (
    <div className='grid'>
      <div className='col-12 col-md-3'>
        <SectionCard>
          {foundUser && (
            <>
              <div className={`content-middle ${styles[`${block}__profile-pic`]}`}>
                <ProfilePicture name={foundUser.fullName!} url={foundUser.photoUrl!} size='large' />
              </div>
              <div className={`text-with-icon ${styles[`${block}__data`]}`}>
                {Icons.SingleUser} {foundUser.fullName}
              </div>
              <div className={`text-with-icon ${styles[`${block}__data`]}`}>
                <span className='black'>{Icons.Mail}</span>
                {!copied && (
                  <button className={`${styles[`${block}__data--email`]}`} type='button' onClick={clickProfileHandler}>
                    <span className='kg-blue'>{foundUser.email}</span>
                  </button>
                )}
                {copied && <span className='disabled-text'>Email Copied!</span>}
              </div>
              <div className={`text-with-icon ${styles[`${block}__data`]}`}>
                {Icons.LocationPin} {foundUser?.office?.Name}
              </div>
              <div className={`text-with-icon ${styles[`${block}__data`]}`}>
                {Icons.Building} {foundUser?.company?.Name}
              </div>
            </>
          )}
          <hr className='divider' />
          <Button
            type='primary'
            className='button-with-icon'
            fullWidth
            onClick={handleUnassignAll}
            disabled={!userHasAssets}>
            {Icons.Delete} Remove All Assets
          </Button>
          <br />
          <Button type='secondary' fullWidth onClick={() => setShowOffboardingModal(true)} className='button-with-icon'>
            {Icons.Book} Offboarding PDF
          </Button>
        </SectionCard>
      </div>
      <div className='col-12 col-md-9'>
        <ListCard<Asset>
          data={userAssetsData}
          detailsPath={PATHS.ASSETS}
          loading={loading}
          error={errorMessage}
          title={'Current Assets'}
          structure={TABLE_STRUCTURES.ASSETS_USER}
          withActions={{
            unassign: (assetId) => handleUnassign(assetId, ELEMENT_TO_DELETE_TYPE.ASSET),
            view: (assetId) => navigate(`${PATHS.ASSETS}/${assetId}`),
          }}
        />
        <div className='col-12 content-middle content-right-md'>
          <Pagination
            count={userAssets.docs.length}
            perPage={PAGE_SIZE}
            currentPage={assetsCurrentPage - 1}
            onChange={(page) => {
              setAssetsCurrentPage(page + 1);
              userAssetsJump(page + 1);
            }}
          />
        </div>
        <br />
        <ListCard<Accessory>
          data={userAccessoriesData}
          detailsPath={PATHS.ACCESSORIES}
          loading={loading}
          error={errorMessage}
          title={'Accessories'}
          structure={TABLE_STRUCTURES.ACCESSORY}
          withActions={{
            unassign: (accessoryId) => handleUnassign(accessoryId, ELEMENT_TO_DELETE_TYPE.ACCESSORY),
            view: (accessoryId) => navigate(`${PATHS.ACCESSORIES}/${accessoryId}`),
          }}
        />
        <div className='col-12 content-middle content-right-md'>
          <Pagination
            count={userAccessories.docs.length}
            perPage={PAGE_SIZE}
            currentPage={accessoriesCurrentPage - 1}
            onChange={(page) => {
              setAccessoriesCurrentPage(page + 1);
              userAccessoriesJump(page + 1);
            }}
          />
        </div>
        <br />
        <ListCard<UserAssetHistory>
          data={assetHistoryData}
          detailsPath={PATHS.ASSETS}
          withDetails
          loading={loading}
          error={errorMessage}
          title={'Asset History'}
          structure={TABLE_STRUCTURES.USER_ASSET_HISTORY}
        />
        <div className='col-12 content-middle content-right-md'>
          <Pagination
            count={assetHistory.docs.length}
            perPage={PAGE_SIZE}
            currentPage={assetHistoryCurrentPage - 1}
            onChange={(page) => {
              setAssetHistoryCurrentPage(page + 1);
              assetHistoryJump(page + 1);
            }}
          />
        </div>
      </div>

      <OffboardingPDFModal
        show={showOffboardingModal}
        onClose={() => setShowOffboardingModal(false)}
        onSuccess={() => {}}
        user={foundUser!}
      />
      <AssetUnassignModal
        title={'Remove Owner'}
        showModal={unassingModalOpen}
        onClose={handleUnassignModalClose}
        employeeEmail={foundUser?.email!}
        elementsToDelete={elementsToDelete}
        onSuccess={getUserAssets}
        description={'Are you sure you want to unassign this asset from its current user?'}
      />
    </div>
  );
};
export default UserDetails;
